import { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * Error Boundary Component
 *
 * 捕獲子組件樹中的 JavaScript 錯誤，記錄錯誤並顯示降級 UI
 *
 * 使用方式：
 * <ErrorBoundary>
 *   <YourComponent />
 * </ErrorBoundary>
 *
 * 或自訂錯誤 UI：
 * <ErrorBoundary fallback={<CustomErrorUI />}>
 *   <YourComponent />
 * </ErrorBoundary>
 */
class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    // 更新狀態，下次渲染時顯示降級 UI
    return {
      hasError: true,
      error
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // 記錄錯誤到控制台
    console.error('❌ Error Boundary 捕獲錯誤:', {
      error,
      errorInfo,
      componentStack: errorInfo.componentStack
    });

    // 更新狀態以顯示錯誤詳情
    this.setState({
      error,
      errorInfo
    });

    // 如果提供了自訂錯誤處理函數，則調用
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // TODO: 可以在這裡發送錯誤到監控服務
    // 例如：Sentry, LogRocket, Firebase Crashlytics 等
    // logErrorToService(error, errorInfo);
  }

  handleReset = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      // 如果提供了自訂降級 UI，使用它
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // 預設錯誤 UI
      return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 p-4">
          <div className="max-w-2xl w-full bg-white rounded-2xl shadow-xl overflow-hidden">
            {/* 頂部警告條 */}
            <div className="bg-gradient-to-r from-red-500 to-red-600 px-6 py-4">
              <div className="flex items-center gap-3">
                <div className="text-5xl">⚠️</div>
                <div>
                  <h1 className="text-2xl font-bold text-white">
                    系統發生錯誤
                  </h1>
                </div>
              </div>
            </div>

            {/* 內容區域 */}
            <div className="p-8">
              {/* 錯誤詳情（開發模式） */}
              {import.meta.env.DEV && this.state.error && (
                <details className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                  <summary className="cursor-pointer font-semibold text-red-700 hover:text-red-800 select-none">
                    🔍 錯誤詳情（開發模式）
                  </summary>
                  <div className="mt-4 space-y-3">
                    <div>
                      <p className="text-xs font-semibold text-red-600 mb-1">
                        錯誤訊息：
                      </p>
                      <pre className="text-xs text-red-700 bg-red-100 p-3 rounded overflow-x-auto">
                        {this.state.error.toString()}
                      </pre>
                    </div>
                    {this.state.errorInfo && (
                      <div>
                        <p className="text-xs font-semibold text-red-600 mb-1">
                          組件堆疊：
                        </p>
                        <pre className="text-xs text-red-700 bg-red-100 p-3 rounded overflow-x-auto whitespace-pre-wrap">
                          {this.state.errorInfo.componentStack}
                        </pre>
                      </div>
                    )}
                  </div>
                </details>
              )}

              {/* 操作按鈕 */}
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <button
                  onClick={this.handleReset}
                  className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium shadow-md hover:shadow-lg"
                >
                  🔄 重試
                </button>
                <button
                  onClick={() => window.location.reload()}
                  className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors font-medium shadow-md hover:shadow-lg"
                >
                  🔃 重新整理頁面
                </button>
                <button
                  onClick={() => window.location.href = '/'}
                  className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium shadow-md hover:shadow-lg"
                >
                  🏠 返回首頁
                </button>
              </div>

              {/* 技術支援資訊 */}
              <div className="mt-8 pt-6 border-t border-gray-200 text-center">
                <p className="text-sm text-gray-500">
                  重新整理後仍無法使用，請聯繫技術支援。
                </p>
                {import.meta.env.DEV && (
                  <p className="text-xs text-gray-400 mt-2">
                    錯誤時間：{new Date().toLocaleString('zh-TW')}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      );
    }

    // 正常渲染子組件
    return this.props.children;
  }
}

export default ErrorBoundary;
